import {
  SubscriptionCycleRecord,
  ArenaCycleStatus,
} from "./types/subscription.types";
import { SubscriptionService } from "./SubscriptionService";

export interface OperationalCycleResolution {
  model: string;
  planId: string;
  cycleId?: string;
  origin: "PAID_CYCLE" | "SPLIT_FALLBACK";
  startDate?: string;
  endDate?: string;
  monthlyAmount?: number;
  isCourtesy?: boolean;
}

function normalizeDate(d: string): string {
  // Converte YYYY-MM-DD para o formato comparável (YYYY-MM-DDT00:00:00Z)
  return d.length === 10 ? d + "T00:00:00Z" : d;
}

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

/**
 * Resolve o ciclo comercial operacional vigente para uma Arena.
 * Determina se a Arena opera em um ciclo pago (FIXED_MONTHLY/HYBRID), ciclo de cortesia ou em fallback para SPLIT.
 * Respeita a janela de tolerância de 4 HORAS pós-vencimento antes de transicionar para o fallback SPLIT.
 */
export async function resolveOperationalCommercialCycle(
  arenaId: string,
  now: Date = new Date()
): Promise<OperationalCycleResolution> {
  const nowIso = now.toISOString();

  // 1. Busca todos os ciclos históricos
  const history = await SubscriptionService.getSubscriptionCycleHistory(arenaId);
  
  // 2. Filtra ciclos vigentes (startDate <= now <= endDate + 4 HORAS)
  const vigente = history.filter((c) => {
    const startDate = normalizeDate(c.startDate);
    const endDate = c.endDate ? normalizeDate(c.endDate) : null;
    const statusVigente: ArenaCycleStatus[] = ["CURRENT", "PAID", "GRACE", "GRACE_PERIOD"];
    
    let isWithin4HoursTolerance = false;
    if (endDate) {
      const endMs = endDate.includes("T")
        ? new Date(endDate).getTime()
        : new Date(endDate + "T23:59:59.999Z").getTime();
      const diffMs = now.getTime() - endMs;
      isWithin4HoursTolerance = diffMs > 0 && diffMs <= FOUR_HOURS_MS;
    }

    const isDateVigente = startDate <= nowIso && (endDate ? (endDate > nowIso || isWithin4HoursTolerance) : true);

    return (
      isDateVigente &&
      (c.commercialModel === "FIXED_MONTHLY" || c.commercialModel === "HYBRID") &&
      statusVigente.includes(c.status as ArenaCycleStatus)
    );
  });

  // 3. Seleção determinística
  if (vigente.length > 0) {
    // Ordem:
    // 1. Ciclo de cortesia ativo concedido tem prioridade operacional se vigente
    // 2. Mais recentemente ativado (startDate decrescente)
    // 3. Empate: cycleId (desempate)
    vigente.sort((a, b) => {
      const aCourtesy = Boolean(a.isCourtesy);
      const bCourtesy = Boolean(b.isCourtesy);
      if (aCourtesy !== bCourtesy) {
        return aCourtesy ? -1 : 1;
      }
      if (b.startDate !== a.startDate) {
        return b.startDate.localeCompare(a.startDate);
      }
      return (b.cycleId || "").localeCompare(a.cycleId || "");
    });

    const chosen = vigente[0];
    return {
      model: chosen.commercialModel || "FIXED_MONTHLY",
      planId: chosen.commercialPlanId || "fixed_1_2",
      cycleId: chosen.cycleId,
      origin: "PAID_CYCLE",
      startDate: chosen.startDate,
      endDate: chosen.endDate,
      monthlyAmount: chosen.monthlyAmount,
      isCourtesy: Boolean(chosen.isCourtesy),
    };
  }

  // 4. Fallback SPLIT
  return {
    model: "SPLIT",
    planId: "split_1_open",
    origin: "SPLIT_FALLBACK",
  };
}
